/**
 * Cooperative single-owner election across tabs and popouts.
 *
 * A browser cannot enforce one window the way a Mac scene can, so this elects a
 * leader instead of forbidding a second document. The part that has to port is
 * the part that matters: **one** live effort authority writing **one**
 * checkpoint, and a second attempt that focuses the surface that already exists
 * rather than becoming a second owner of the same workout.
 *
 * Two mechanisms, in this order:
 *
 * 1. **Web Locks.** `navigator.locks.request(name, { ifAvailable: true })` is
 *    decided by the browser, not by a timeout, and the lock is released when the
 *    document that holds it dies — including a crash, which is the case a
 *    heartbeat protocol handles worst.
 * 2. **`BroadcastChannel` probe.** Where Web Locks are absent (older Safari), a
 *    claim is broadcast and the incumbent answers. This one *is* timing-based
 *    and can therefore be wrong under a genuine simultaneous claim, which is why
 *    it is the fallback and not the mechanism.
 *
 * The channel is opened either way, because Web Locks can only tell you a lock
 * is taken — it cannot ask the holder to bring itself forward, and that request
 * is what `focusExisting` exists to deliver.
 *
 * With neither API the election grants every claim. That is deliberate: the
 * alternative is refusing to run a workout in a browser that merely lacks a
 * coordination primitive, which trades a rare double-window for a guaranteed
 * dead end.
 */

import type { Milliseconds } from "@ff/domain";
import type { Cancel, LeaderElectionPort, LeadershipClaim } from "@ff/domain/ports";

/** One channel for every role; the role travels in the message. */
export const LEADER_CHANNEL_NAME = "fighting-form.leader";

/** Web Lock names are a flat origin-wide namespace shared with any other script. */
export const LEADER_LOCK_PREFIX = "fighting-form.leader.";

/**
 * How long a probe waits for the incumbent to answer.
 *
 * `BroadcastChannel` delivery within one origin is a task hop, not a network
 * round trip, so this is generous. It is still the reason the probe is the
 * fallback: every claim in a browser without Web Locks costs this much before
 * the first tab may start.
 */
export const LEADER_PROBE_WINDOW: Milliseconds = 100;

export interface LockManagerLike {
  request(
    name: string,
    options: { readonly ifAvailable: true },
    callback: (lock: unknown) => Promise<void>,
  ): Promise<unknown>;
}

export interface LeaderChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: { readonly data: unknown }) => void): void;
  close(): void;
}

export interface LeaderElectionEnvironment {
  readonly locks: LockManagerLike | undefined;
  /** `undefined` where `BroadcastChannel` is missing or refused. */
  createChannel(name: string): LeaderChannelLike | undefined;
  /** Brings this document forward. A no-op in browsers that refuse it. */
  focusSelf(): void;
  after(delay: Milliseconds, callback: () => void): Cancel;
  readonly probeWindow: Milliseconds;
  /** Distinguishes this document's messages from another's. */
  participantId(): string;
}

export function browserLeaderElectionEnvironment(): LeaderElectionEnvironment {
  return {
    locks: resolveLockManager(),
    createChannel: (name) => {
      const constructor = (globalThis as { BroadcastChannel?: unknown }).BroadcastChannel;
      if (typeof constructor !== "function") return undefined;
      try {
        return wrapBroadcastChannel(new (constructor as new (name: string) => object)(name));
      } catch {
        // Blocked storage partitioning and some privacy modes refuse the
        // constructor. Losing the channel costs `focusExisting`, not the app.
        return undefined;
      }
    },
    focusSelf: () => {
      const focus = (globalThis as { focus?: unknown }).focus;
      // A background tab is usually not allowed to raise itself. The port says
      // `true` means the request was delivered, never that the user saw it.
      if (typeof focus === "function") (focus as () => void).call(globalThis);
    },
    after: (delay, callback) => {
      const handle = globalThis.setTimeout(callback, delay);
      return () => {
        globalThis.clearTimeout(handle);
      };
    },
    probeWindow: LEADER_PROBE_WINDOW,
    participantId: () => randomIdentifier(),
  };
}

type LeaderMessageKind = "probe" | "focus" | "here";

interface LeaderMessage {
  readonly kind: LeaderMessageKind;
  readonly role: string;
  readonly nonce: string;
}

/**
 * Web Lock releases this document has started but the browser has not finished.
 *
 * A lock is freed when the promise the holder returned settles, which is at
 * least one turn *after* `release()` returns to its caller. A claim that lands
 * in that gap is told the role is taken by a holder that has already given it
 * up, and a surface handing leadership to its replacement does exactly that. So
 * a claim waits for a release of the same lock that this document already
 * started. Module scope is document scope on the web, which is the right
 * boundary: another tab's release is not visible here and is not raced either.
 */
const pendingLockReleases = new Map<string, Promise<unknown>>();

interface LedRole {
  readonly release: Cancel;
  /** Releases the underlying Web Lock, if the claim came from one. */
  readonly releaseLock: (() => void) | undefined;
}

export class BrowserLeaderElection implements LeaderElectionPort {
  readonly #environment: LeaderElectionEnvironment;
  readonly #channel: LeaderChannelLike | undefined;
  readonly #participantId: string;
  readonly #led = new Map<string, LedRole>();
  /** In-flight claims, so two callers asking at once contend once, not twice. */
  readonly #claiming = new Map<string, Promise<LeadershipClaim>>();
  /** Outstanding probes, keyed by the nonce the answer will carry. */
  readonly #awaitingAnswer = new Map<string, () => void>();
  #nonceCounter = 0;

  constructor(environment: LeaderElectionEnvironment = browserLeaderElectionEnvironment()) {
    this.#environment = environment;
    this.#participantId = environment.participantId();
    this.#channel = environment.createChannel(LEADER_CHANNEL_NAME);
    this.#channel?.addEventListener("message", (event) => {
      this.#receive(event.data);
    });
  }

  async claim(role: string): Promise<LeadershipClaim> {
    const existing = this.#led.get(role);
    // Re-claiming a role this document already leads returns leadership rather
    // than deposing itself; a document that released on every re-claim would
    // hand the role to the next tab that asked.
    if (existing !== undefined) return { kind: "leader", release: existing.release };

    const inFlight = this.#claiming.get(role);
    if (inFlight !== undefined) return inFlight;

    const attempt = this.#attempt(role);
    this.#claiming.set(role, attempt);
    try {
      return await attempt;
    } finally {
      this.#claiming.delete(role);
    }
  }

  async focusExisting(role: string): Promise<boolean> {
    if (this.#led.has(role)) {
      this.#environment.focusSelf();
      return true;
    }
    // The answer doubles as the acknowledgement: the incumbent focuses itself
    // when it receives this, so a reply means the request was delivered to a
    // document that exists.
    return this.#ask("focus", role);
  }

  /** Releases every role and closes the channel. For a closing popout, or a test. */
  dispose(): void {
    for (const role of [...this.#led.keys()]) this.#led.get(role)?.release();
    for (const answer of [...this.#awaitingAnswer.values()]) answer();
    this.#awaitingAnswer.clear();
    this.#channel?.close();
  }

  async #attempt(role: string): Promise<LeadershipClaim> {
    const locks = this.#environment.locks;
    const releaseLock =
      locks === undefined ? undefined : await this.#claimWebLock(locks, role);

    if (locks !== undefined) {
      if (releaseLock === undefined) return { kind: "follower" };
    } else if (await this.#ask("probe", role)) {
      // Somebody answered, so somebody leads.
      return { kind: "follower" };
    }

    const release: Cancel = () => {
      // Identity-checked: a release from a claim that has already been
      // superseded must not evict the holder that replaced it. That is exactly
      // how two documents end up both believing they own the live effort.
      if (this.#led.get(role) !== entry) return;
      this.#led.delete(role);
      entry.releaseLock?.();
    };
    const entry: LedRole = { release, releaseLock };
    this.#led.set(role, entry);
    return { kind: "leader", release };
  }

  /**
   * Resolves with a release function when the lock was granted, `undefined` when
   * another document holds it.
   */
  async #claimWebLock(
    locks: LockManagerLike,
    role: string,
  ): Promise<(() => void) | undefined> {
    const lockName = `${LEADER_LOCK_PREFIX}${role}`;
    await pendingLockReleases.get(lockName);

    let releaseHold: () => void = () => {};
    const holding = new Promise<void>((resolve) => {
      releaseHold = resolve;
    });

    let settle: (granted: boolean) => void = () => {};
    const granted = new Promise<boolean>((resolve) => {
      settle = resolve;
    });

    const request = locks
      .request(lockName, { ifAvailable: true }, async (lock) => {
        if (lock === null || lock === undefined) {
          settle(false);
          return;
        }
        settle(true);
        // The lock is held for exactly as long as this promise is pending, which
        // is what ties leadership to the document's life: if the tab dies, the
        // browser resolves it and the role frees itself with no heartbeat and no
        // stale-lock sweep.
        await holding;
      })
      .catch(() => {
        settle(false);
      });

    if (!(await granted)) return undefined;

    return () => {
      releaseHold();
      // `request` resolves once the browser has taken the lock back, so this is
      // the signal a following claim waits on rather than a guessed delay.
      const settling = request.finally(() => {
        if (pendingLockReleases.get(lockName) === settling) {
          pendingLockReleases.delete(lockName);
        }
      });
      pendingLockReleases.set(lockName, settling);
    };
  }

  #ask(kind: "probe" | "focus", role: string): Promise<boolean> {
    const channel = this.#channel;
    // No channel: nobody can answer, so nobody is there as far as this document
    // can tell. For a probe that means "claim it"; for a focus request it means
    // the caller must show its own fallback, which the port requires anyway.
    if (channel === undefined) return Promise.resolve(false);

    const nonce = `${this.#participantId}:${(this.#nonceCounter += 1)}`;
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let cancelWindow: Cancel | undefined;

      const finish = (answered: boolean): void => {
        if (settled) return;
        settled = true;
        this.#awaitingAnswer.delete(nonce);
        cancelWindow?.();
        resolve(answered);
      };

      this.#awaitingAnswer.set(nonce, () => {
        finish(true);
      });
      cancelWindow = this.#environment.after(this.#environment.probeWindow, () => {
        finish(false);
      });
      // Posted last, so an environment that delivers synchronously still finds
      // the nonce registered and the window cancellable.
      channel.postMessage({ kind, role, nonce } satisfies LeaderMessage);
    });
  }

  #receive(data: unknown): void {
    const message = parseLeaderMessage(data);
    if (message === undefined) return;

    if (message.kind === "here") {
      this.#awaitingAnswer.get(message.nonce)?.();
      return;
    }

    // Only the incumbent answers. A follower that replied would make every
    // claimant see an answer and stand down, leaving the role permanently
    // unclaimed.
    if (!this.#led.has(message.role)) return;
    if (message.kind === "focus") this.#environment.focusSelf();
    this.#channel?.postMessage({
      kind: "here",
      role: message.role,
      nonce: message.nonce,
    } satisfies LeaderMessage);
  }
}

function parseLeaderMessage(data: unknown): LeaderMessage | undefined {
  // A `BroadcastChannel` in this origin can carry anything any script posted;
  // the shape is checked rather than asserted so a stray message from another
  // tool cannot depose a leader.
  if (typeof data !== "object" || data === null) return undefined;
  const candidate = data as { kind?: unknown; role?: unknown; nonce?: unknown };
  if (candidate.kind !== "probe" && candidate.kind !== "focus" && candidate.kind !== "here") {
    return undefined;
  }
  if (typeof candidate.role !== "string" || typeof candidate.nonce !== "string") {
    return undefined;
  }
  return { kind: candidate.kind, role: candidate.role, nonce: candidate.nonce };
}

function resolveLockManager(): LockManagerLike | undefined {
  const candidate = (globalThis.navigator as { locks?: unknown } | undefined)?.locks;
  if (candidate === undefined || candidate === null) return undefined;
  const request = (candidate as { request?: unknown }).request;
  if (typeof request !== "function") return undefined;

  const manager = candidate as {
    request: (
      name: string,
      options: { readonly ifAvailable: true },
      callback: (lock: unknown) => Promise<void>,
    ) => Promise<unknown>;
  };
  // Wrapped rather than handed over directly: `LockManager.request` is a wide
  // overload set, and narrowing it here keeps the rest of this file honest about
  // the single form it actually uses.
  return {
    request: (name, options, callback) => manager.request(name, options, callback),
  };
}

function wrapBroadcastChannel(channel: object): LeaderChannelLike {
  const raw = channel as {
    postMessage: (message: unknown) => void;
    addEventListener: (type: string, listener: (event: { data: unknown }) => void) => void;
    close: () => void;
  };
  return {
    postMessage: (message) => {
      try {
        raw.postMessage(message);
      } catch {
        // A closed channel throws. Losing the message costs a focus request, and
        // the caller already has to handle `false`.
      }
    },
    addEventListener: (type, listener) => {
      raw.addEventListener(type, listener);
    },
    close: () => {
      try {
        raw.close();
      } catch {
        // Already closed.
      }
    },
  };
}

function randomIdentifier(): string {
  const cryptoSource = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof cryptoSource?.randomUUID === "function") return cryptoSource.randomUUID();
  // Only has to be unlikely to collide with the handful of documents one user
  // has open; nothing here is a security boundary.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
